import React, { MouseEvent, useCallback, useMemo } from 'react';
import Markdown, { type MarkdownToJSX } from 'markdown-to-jsx';
import { Box, styled, useTheme } from '@mui/material';

import headerLogoIcon from '../../../../assets/header-logo.svg';
import { MESSAGE_PADDING } from '../../constants';
import { messageBubblePositionToRadiusMap } from '../../helpers';
import {
  getMessageBubblePositions,
  MessageBubblePosition,
} from '../../StepWrapper/helpers';
import {
  selectLegibleElementColor,
  selectSourcesText,
} from '../../StepWrapper/selectors';
import { useStepContext } from '../../StepWrapper/StepWrapper';
import { Size, UserMessageComponent } from '../../types';
import { appendAnalyticsParamsToURL } from '../../UtilComponents/Article/Article';
import { AuthorIcon } from '../AgathaMessage/AgathaMessage';

interface Props {
  agentIcon?: string;
  isAdminOnly?: boolean;
  isAuthorIconVisible?: boolean;
  isLiveChat?: boolean;
  message: string;
  renderSingleNewLineCharacters?: boolean;
  widgetComponent?: UserMessageComponent;
}

const IrArticleTextMessage: React.FC<React.PropsWithChildren<Props>> = ({
  agentIcon,
  isAdminOnly = false,
  isAuthorIconVisible = false,
  isLiveChat = false,
  message,
  renderSingleNewLineCharacters = false,
  widgetComponent,
}) => {
  const context = useStepContext();
  const {
    agentChatIcon,
    customConfigParams,
    isMobile,
    liveChatAgentChatIcon,
    openArticleInWidget,
    sendTrackingEvent,
    size,
  } = context;
  const sourcesText = selectSourcesText(context);
  const theme = useTheme();
  const legibleElementColor = selectLegibleElementColor(context);
  const hideNav = customConfigParams?.hide_nav ?? '';
  const knowledgeArticles =
    widgetComponent?.component_fields.knowledge_articles;
  const readArticleAction =
    widgetComponent?.component_fields.read_article_action;
  const articleStepData = widgetComponent?.component_fields.article_step_data;
  const openInWidget =
    hideNav !== 'true' && readArticleAction === 'expand_in_widget';

  const sendArticleTrackingEvent = useCallback(
    (
      documentID: string,
      documentIndex: number,
      traceId?: string,
      spanId?: string,
      standardized_document_id?: string,
    ) => {
      sendTrackingEvent({
        current_step_id: articleStepData?.current_step_id,
        doc_id: documentID,
        doc_index: documentIndex,
        event_type: 'go-to-article',
        intent_workflow_id: articleStepData?.intent_workflow_id,
        intent_workflow_version: articleStepData?.intent_workflow_version,
        org_id: articleStepData?.org_id,
        span_id: spanId,
        standardized_document_id,
        trace_id: traceId,
      });
    },
    [
      articleStepData?.current_step_id,
      articleStepData?.intent_workflow_id,
      articleStepData?.intent_workflow_version,
      articleStepData?.org_id,
      sendTrackingEvent,
    ],
  );

  const getMatchingArticleAndIndex = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!knowledgeArticles) {
        return;
      }

      const container = event.currentTarget.closest('.js-bot-message');

      if (!container) {
        return;
      }

      const links = Array.from(container.querySelectorAll('a'));
      const index = links.findIndex(link => link === event.currentTarget);

      if (index === -1) {
        return;
      }

      const article = knowledgeArticles[index];

      if (!article) {
        return;
      }

      return { article, index } as const;
    },
    [knowledgeArticles],
  );

  // Calculate current link index via the parent container, and open matching article:
  const handleOpenInWidgetLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const matchingArticleAndIndex = getMatchingArticleAndIndex(event);

      if (!matchingArticleAndIndex) {
        return;
      }

      const { article, index } = matchingArticleAndIndex;

      openArticleInWidget(article, index);
      sendArticleTrackingEvent(
        article.document_id,
        index,
        article.trace_id,
        article.span_id,
        article.standardized_article_id,
      );
    },
    [getMatchingArticleAndIndex, openArticleInWidget, sendArticleTrackingEvent],
  );

  // Calculate current link index via the parent container, and send tracking
  // event for the matching article:
  const handleNewTabLinkClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      const matchingArticleAndIndex = getMatchingArticleAndIndex(event);

      if (!matchingArticleAndIndex) {
        return;
      }

      const { article, index } = matchingArticleAndIndex;

      sendArticleTrackingEvent(
        article.document_id,
        index,
        article.trace_id,
        article.span_id,
        article.standardized_article_id,
      );
    },
    [getMatchingArticleAndIndex, sendArticleTrackingEvent],
  );
  const markdownOptions: MarkdownToJSX.Options = useMemo(
    () => ({
      overrides: {
        a: {
          props: {
            ...((knowledgeArticles?.length || 0) > 0
              ? {
                  onClick: openInWidget
                    ? handleOpenInWidgetLinkClick
                    : handleNewTabLinkClick,
                }
              : {}),
            rel: 'noreferrer',
            target: '_blank',
          },
        },
      },
      wrapper: React.Fragment,
    }),
    [
      handleNewTabLinkClick,
      handleOpenInWidgetLinkClick,
      knowledgeArticles?.length,
      openInWidget,
    ],
  );

  const formattedMessage = useMemo(() => {
    let processedMessage = message;

    /**
     * https://www.markdownguide.org/basic-syntax/#line-breaks
     *
     * lookbehind is currently like 94% supported - https://caniuse.com/js-regexp-lookbehind
     */
    if (renderSingleNewLineCharacters) {
      try {
        /**
         * literal notation will throw errors when loading the app in the browser
         * causing the app to not load. RegExp constructor will throw a syntax error
         * getting caught by the try/catch block.
         */
        const regex = new RegExp('(?<!\\n)\\n(?!\\n)', 'g');
        processedMessage = processedMessage.replace(regex, '  \n');
      } catch {
        // Do nothing (browser doesn't support lookbehind/lookahead)
      }
    }

    return processedMessage;
  }, [message, renderSingleNewLineCharacters]);

  const getAgentChatIcon = () => {
    if (isAdminOnly) {
      return undefined;
    }

    if (isLiveChat) {
      return agentIcon ?? liveChatAgentChatIcon ?? headerLogoIcon;
    }

    return agentChatIcon;
  };

  function getLinks() {
    if (
      readArticleAction === 'none' ||
      !knowledgeArticles ||
      (!openInWidget && knowledgeArticles.every(article => !article.link))
    ) {
      return null;
    }

    function getMessageBubblePosition(index: number): MessageBubblePosition {
      if (!knowledgeArticles) {
        return 'lone';
      }

      if (knowledgeArticles?.length === 1) {
        return 'lone';
      }

      if (index === 0) {
        return 'first-in-a-batch';
      }

      if (index === knowledgeArticles?.length - 1) {
        return 'last-in-a-batch';
      }

      return 'middle';
    }

    return (
      <>
        <Box marginBottom='12px'>{sourcesText}:</Box>
        {knowledgeArticles?.map((article, articleIndex) => {
          if (openInWidget) {
            return (
              <Box
                key={article.document_id}
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flexDirection: 'row',
                  margin: `0 24px 16px ${
                    isAuthorIconVisible &&
                    articleIndex === knowledgeArticles.length - 1
                      ? '-24px'
                      : '0'
                  }`,
                }}
              >
                {isAuthorIconVisible &&
                  articleIndex === knowledgeArticles.length - 1 && (
                    <AuthorIcon
                      icon={getAgentChatIcon()}
                      isLiveChat={isLiveChat}
                    />
                  )}
                <AgathaMessageContainer
                  backgroundColor={theme.palette.colors.slate[100]}
                  forethoughtLiveChat={false}
                  isMobile={isMobile}
                  key={article.document_id}
                  marginBottom={
                    articleIndex === knowledgeArticles.length - 1
                      ? '16px'
                      : '4px'
                  }
                  messageBubblePosition={{
                    bot: getMessageBubblePosition(articleIndex),
                  }}
                  size={size}
                >
                  <ReadArticleLink
                    aria-label={`${sourcesText}: ${article.title}`}
                    as='button'
                    data-testid={`read-article-link-${articleIndex}`}
                    onClick={() => {
                      openArticleInWidget(article, articleIndex);
                      sendArticleTrackingEvent(
                        article.document_id,
                        articleIndex,
                        article.trace_id,
                        article.span_id,
                        article.standardized_article_id,
                      );
                    }}
                    themeColor={legibleElementColor}
                  >
                    {article.title}
                  </ReadArticleLink>
                </AgathaMessageContainer>
              </Box>
            );
          } else if (article.link) {
            return (
              <Box
                key={article.document_id}
                sx={{
                  alignItems: 'flex-end',
                  display: 'flex',
                  flexDirection: 'row',
                  margin: `0 24px 16px ${
                    isAuthorIconVisible &&
                    articleIndex === knowledgeArticles.length - 1
                      ? '-24px'
                      : '0'
                  }`,
                }}
              >
                {isAuthorIconVisible &&
                  articleIndex === knowledgeArticles.length - 1 && (
                    <AuthorIcon
                      icon={getAgentChatIcon()}
                      isLiveChat={isLiveChat}
                    />
                  )}
                <AgathaMessageContainer
                  backgroundColor={theme.palette.colors.slate[100]}
                  forethoughtLiveChat={false}
                  isMobile={isMobile}
                  messageBubblePosition={{
                    bot: getMessageBubblePosition(articleIndex),
                  }}
                  size={size}
                >
                  <ReadArticleLink
                    aria-label={`${sourcesText}: ${article.title}`}
                    data-testid={`read-article-link-${articleIndex}`}
                    href={appendAnalyticsParamsToURL(article.link)}
                    key={article.document_id}
                    onClick={() => {
                      sendArticleTrackingEvent(
                        article.document_id,
                        articleIndex,
                        article.span_id,
                        article.trace_id,
                        article.standardized_article_id,
                      );
                    }}
                    rel='noreferrer'
                    target='_blank'
                    themeColor={legibleElementColor}
                  >
                    {article.title}
                  </ReadArticleLink>
                </AgathaMessageContainer>
              </Box>
            );
          } else {
            return null;
          }
        })}
      </>
    );
  }

  return knowledgeArticles ? (
    <Box marginInline='24px'>{getLinks()}</Box>
  ) : (
    <Markdown options={markdownOptions}>{formattedMessage}</Markdown>
  );
};

const AgathaMessageContainer = styled('div')<{
  backgroundColor: string;
  forethoughtLiveChat: boolean;
  hasLink?: boolean;
  hideBackground?: boolean;
  isMobile: boolean;
  marginBottom?: string;
  messageBubblePosition: ReturnType<typeof getMessageBubblePositions>[string];
  size: Size;
}>`
  white-space: normal;
  position: relative;
  padding: ${MESSAGE_PADDING};
  background-color: ${props => props.backgroundColor};
  border-radius: ${props =>
    props.messageBubblePosition?.bot
      ? messageBubblePositionToRadiusMap[props.messageBubblePosition?.bot]
      : '25px'};
  width: fit-content;
  align-self: none;
  min-height: 22px;
  word-break: break-word;
  max-width: ${props =>
    props.size === 'large' && !props.isMobile
      ? 'calc(100% - 112px)'
      : 'calc(100% - 16px - 4px)'};

  ${props =>
    props.forethoughtLiveChat &&
    !props.hideBackground &&
    `background-image: linear-gradient(
      90deg,#EBF0FE,#F8E6FF
    );`}

  ${props =>
    (props.messageBubblePosition?.bot === 'first-in-a-batch' ||
      props.messageBubblePosition?.bot === 'middle') &&
    !props.hasLink &&
    'margin-bottom: -12px;'}

  ${props => props.marginBottom && `margin-bottom: ${props.marginBottom}`}
`;

const ReadArticleLink = styled('a')<{ themeColor: string }>`
  color: ${props => props.themeColor};
  font-size: 16px;
  display: block;
  text-align: initial;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
  padding-bottom: 0;
  margin-top: 0;
  background-color: transparent;
  border: none;
`;

export default IrArticleTextMessage;
